/**
 * CityTexture — desenha o "mapa" da cidade num canvas de alta resolução
 * aplicado ao chão. Ordem: gramado → calçadas (anéis das quadras) → asfalto
 * → faixas/setas → rotatória → sombras de contato.
 *
 * As calçadas são anéis pavimentados junto às ruas; o interior das quadras
 * fica verde (parque), e o asfalto aparece entre as calçadas de quadras
 * vizinhas.
 */

import * as THREE from 'three';
import { COLORS, WORLD, ROADS, isGridLine, isDrivable, TEXTURE_PX_PER_UNIT as PX } from '../config.js';
import {
  BUILDINGS,
  BLOCKS,
  DISTRICTS,
  DISTRICT_BEDS,
  DISTRICT_BED_R,
  SIDEWALK,
  ROUNDABOUT,
  ENTRY_ROADS,
  ENTRY_ROADS_V,
  insideDistrict,
  isPopulated,
  makeRandom
} from './cityLayout.js';

// linhas da GRADE (sem os tocos de acesso): o que indexa coluna/linha — faixas de
// pedestre e setas — tem de casar com a grade dos lotes, não com a lista de asfalto
const GRID_V = ROADS.vertical.filter(isGridLine);
const GRID_H = ROADS.horizontal.filter(isGridLine);

const W = WORLD.width;
const D = WORLD.depth;
const HALF = ROADS.width / 2;

const texX = (x) => (x + W / 2) * PX;
const texY = (z) => (z + D / 2) * PX;
const u = (units) => units * PX;
/**
 * FATOR DE DENSIDADE em relação à calibração original (PX = 9). Quase tudo neste
 * arquivo já desenha em UNIDADES DE MUNDO via u(), e acompanha o PX sozinho — as
 * duas exceções são o granulado do gramado e o do asfalto, que sorteiam retângulos
 * em PIXELS ABSOLUTOS de canvas. Sem corrigi-los, subir o PX (nível Ultra) faria o
 * grão encolher e rarear por área de mundo, e o chão ficaria chapado/plástico: uma
 * regressão silenciosa, já que o gerador é determinístico e nenhum harness olha a
 * aparência. Contagem escala com o QUADRADO (é área), tamanho, linearmente.
 */
const GRAIN_K = PX / 9;
const grainN = (n) => Math.round(n * GRAIN_K * GRAIN_K);

export function createCityTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = W * PX;
  canvas.height = D * PX;
  const ctx = canvas.getContext('2d');
  const rand = makeRandom(7654321);

  drawGrass(ctx, rand);
  drawForestBorder(ctx);
  drawRoads(ctx); // asfalto primeiro
  roundCityEdge(ctx); // arredonda as quinas externas da cidade
  drawSidewalks(ctx, rand); // quadras por cima (canto = asfalto + borda escura)
  drawDistrictBlocks(ctx); // superquadras: 4 quadras viram 1, com parque no miolo
  drawMapBorder(ctx); // borda preta + cinza nas ruas limítrofes (lado externo)
  drawMarkings(ctx);
  drawCrosswalks(ctx);
  drawEntryRoads(ctx); // prolonga as vias laterais (entrada de jogadores)
  drawEntryRoadsV(ctx); // prolonga as vias de cima/baixo (novas entradas)
  drawGrain(ctx, rand); // granulado por cima de TUDO (mesmo tom em todas as vias)
  if (ROUNDABOUT) drawPlaza(ctx); // cidade sem praça (Mexican Town) → nada a desenhar
  drawContactShadows(ctx);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/* ----------------------------- gramado ----------------------------- */

function drawGrass(ctx, rand) {
  const g = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
  g.addColorStop(0, COLORS.grassDark);
  g.addColorStop(0.5, COLORS.grass);
  g.addColorStop(1, COLORS.grassDark);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  const n = grainN(12000);
  for (let i = 0; i < n; i++) {
    const x = rand() * ctx.canvas.width;
    const y = rand() * ctx.canvas.height;
    const s = (1 + rand() * 2.4) * GRAIN_K;
    ctx.fillStyle = rand() > 0.5 ? 'rgba(46,128,76,0.10)' : 'rgba(0,0,0,0.12)';
    ctx.fillRect(x, y, s, s);
  }
}

function drawForestBorder(ctx) {
  const cw = ctx.canvas.width;
  const ch = ctx.canvas.height;
  const band = u(22);
  const grads = [
    ctx.createLinearGradient(0, 0, band, 0),
    ctx.createLinearGradient(cw, 0, cw - band, 0),
    ctx.createLinearGradient(0, 0, 0, band),
    ctx.createLinearGradient(0, ch, 0, ch - band)
  ];
  for (const grad of grads) {
    grad.addColorStop(0, 'rgba(6,18,11,0.9)');
    grad.addColorStop(1, 'rgba(6,18,11,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, cw, ch);
  }
}

/* ---------------------------- calçadas ----------------------------- */

function drawSidewalks(ctx, rand) {
  const sw = u(SIDEWALK);

  for (const block of BLOCKS) {
    // quadra que faz parte de uma SUPERQUADRA sai daqui: ela é desenhada junto com o
    // distrito inteiro (drawDistrictBlocks), sem as ruas internas no meio
    if (insideDistrict(block.land.x, block.land.z)) continue;
    const l = block.land;
    const x = texX(l.left);
    const y = texY(l.top);
    const w = u(l.w);
    const h = u(l.d);

    const rO = u(6);
    const rI = u(3.5);

    // (O asfalto da cidade é contínuo, pintado em drawRoads. Aqui só
    // desenhamos a calçada arredondada por cima; os cantos já são asfalto.)

    // Anel de calçada arredondado
    roundRectPath(ctx, x, y, w, h, rO);
    ctx.fillStyle = COLORS.sidewalk;
    ctx.fill();
    roundRectPath(ctx, x + sw, y + sw, w - 2 * sw, h - 2 * sw, rI);
    ctx.fillStyle = COLORS.grass;
    ctx.fill();

    // Ladrilhos quadrados (recortados dentro do anel)
    ctx.save();
    roundRectPath(ctx, x, y, w, h, rO);
    roundRectPath(ctx, x + sw, y + sw, w - 2 * sw, h - 2 * sw, rI);
    ctx.clip('evenodd');
    drawTileGrid(ctx, x, y, w, h);
    ctx.restore();

    // Borda escura que separa a quadra da rua (acompanha o arredondado)
    ctx.lineWidth = Math.max(2, u(0.8));
    ctx.strokeStyle = COLORS.blockEdge;
    roundRectPath(ctx, x, y, w, h, rO);
    ctx.stroke();
    // Linha clara de bordo na via, 1u para fora, acompanhando o arredondado
    // (substitui as linhas retas, que sumiam nas quinas)
    ctx.lineWidth = Math.max(1, u(0.18));
    ctx.strokeStyle = 'rgba(233,242,236,0.42)';
    roundRectPath(ctx, x - u(1), y - u(1), w + u(2), h + u(2), rO + u(1));
    ctx.stroke();
    // meio-fio interno (junto ao parque)
    ctx.lineWidth = Math.max(1.5, u(0.32));
    ctx.strokeStyle = 'rgba(0,0,0,0.16)';
    roundRectPath(ctx, x + sw, y + sw, w - 2 * sw, h - 2 * sw, rI);
    ctx.stroke();
  }

}

/* -------------------------- superquadras --------------------------- */

/**
 * SUPERQUADRA (cityLayout.DISTRICTS): as 4 quadras de um distrito viram UMA só de
 * 82×82. As ruas internas somem sob a pavimentação e o miolo vira PARQUE — a malha
 * antiga de ruas vira a malha de PASSARELAS, então o desenho da cidade continua
 * legível, só que em escala de pedestre.
 *
 * Composição (distrito de 102×70, prédios de 18×18 nos canteiros de 32×32):
 *   • anel de calçada de 5u no perímetro, igual ao de qualquer quadra — é o que
 *     mantém a superquadra na mesma família visual do resto da cidade;
 *   • CANTEIROS de grama de CANTOS ARREDONDADOS, um por lote (cityLayout.DISTRICT_BEDS
 *     — a mesma conta que CENTRA o prédio nele): o piso é pintado primeiro e a grama
 *     entra por cima, então as passarelas são o NEGATIVO entre os canteiros — é o que
 *     dá aresta arredondada em vez de recorte reto;
 *   • as células-PARQUE recebem o chão de praça das outras cidades (cruz + largo).
 *
 * O que era o LARGO CIRCULAR no encontro das passarelas (com jardim e fonte no meio)
 * saiu junto com a fonte do distrito — ver Decor.createDecor. O `districtPavement`
 * (que é o negativo EXATO deste desenho, e por onde o pedestre anda) foi corrigido
 * junto: enquanto ele ainda pavimentava o largo, a máscara declarava calçada por cima
 * da pegada dos prédios do miolo e os avatares atravessavam o edifício.
 */
function drawDistrictBlocks(ctx) {
  if (!DISTRICTS.length) return;
  const BED_R = u(DISTRICT_BED_R); // arredondamento dos canteiros

  for (const d of DISTRICTS) {
    const l = d.land;
    const x = texX(l.left);
    const y = texY(l.top);
    const w = u(l.w);
    const h = u(l.d);
    const rO = u(6);

    // piso contínuo de TODA a superquadra (anel e passarelas saem daqui, como negativo
    // dos canteiros que entram por cima)
    const paveAll = () => roundRectPath(ctx, x, y, w, h, rO);
    paveAll();
    ctx.fillStyle = COLORS.sidewalk; // base da superquadra = calçada clara (canteiros empacotados: bandas viram um bloco só)
    ctx.fill();
    ctx.save();
    paveAll();
    ctx.clip();
    drawTileGrid(ctx, x, y, w, h);
    ctx.restore();

    // (Sem faixa de calçada separada: a base já é calçada clara e os canteiros estão empacotados,
    //  então a calçada em volta de cada grama se conecta com a do vizinho num bloco claro contínuo.)

    // CANTEIROS de grama deste distrito (por cima da calçada; shopping ocupa 2 células em x)
    ctx.fillStyle = COLORS.grass;
    for (const bed of DISTRICT_BEDS) {
      if (bed.district !== d) continue;
      const g = bed.grass || bed; // prédio: quadrado 26×26 JUSTO; parque: canteiro cheio
      const px = texX(g.left);
      const py = texY(g.top);
      roundRectPath(ctx, px, py, texX(g.right) - px, texY(g.bottom) - py, BED_R);
      ctx.fill();
    }

    // PRAÇAS: as células-parque do distrito recebem o mesmo chão das praças das outras cidades
    //  (cruz pavimentada + largo central circular ladrilhado com contorno) por cima do canteiro
    //  de grama — sobram 4 quadrantes de grama (onde entram as árvores), igual à rotatória.
    for (const b of BUILDINGS) {
      if (b.type !== 'park' || !b.bed || b.bed.district !== d) continue;
      const gg = b.bed.grass || b.bed; // cruz alcança a borda da GRAMA JUSTA (conecta na faixa)
      const reach = Math.max(gg.w, gg.d) / 2;
      // largo ladrilhado no MESMO padrão das outras cidades (R=7): fonte no centro + bancos a ±4.4.
      drawPlazaGround(ctx, b.bed.x, b.bed.z, reach, 7);
    }

    // bordas: contorno da superquadra inteira
    ctx.lineWidth = Math.max(2, u(0.8));
    ctx.strokeStyle = COLORS.blockEdge;
    roundRectPath(ctx, x, y, w, h, rO);
    ctx.stroke();
    ctx.lineWidth = Math.max(1, u(0.18));
    ctx.strokeStyle = 'rgba(233,242,236,0.42)';
    roundRectPath(ctx, x - u(1), y - u(1), w + u(2), h + u(2), rO + u(1));
    ctx.stroke();
  }
}

/** Granulado leve sobre TODA a pavimentação (inclui as ruas laterais, para
 *  ficarem no mesmo tom das internas). Chamado depois das ruas de entrada. */
function drawGrain(ctx, rand) {
  const n = grainN(4000);
  const s = 2 * GRAIN_K;
  for (let i = 0; i < n; i++) {
    const x = rand() * ctx.canvas.width;
    const y = rand() * ctx.canvas.height;
    ctx.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(x, y, s, s);
  }
}

/* ------------------------------ ruas ------------------------------- */

function drawRoads(ctx) {
  // Asfalto CONTÍNUO em toda a área da cidade. As quadras arredondadas são
  // desenhadas por cima (drawSidewalks); assim ruas e cantos ficam no MESMO
  // asfalto, sem quadrados/emendas por quadra. As quinas externas viram
  // floresta em roundCityEdge (arredondando a cidade).
  const b = cityBoundary();
  ctx.fillStyle = COLORS.asphalt;
  ctx.fillRect(b.x, b.y, b.w, b.h);
}

/** Limite externo das vias de perímetro (retângulo arredondado), em px. */
function cityBoundary() {
  const hr = ROADS.width / 2;
  const xs = ROADS.vertical.map((r) => r.x);
  const zs = ROADS.horizontal.map((r) => r.z);
  const left = Math.min(...xs) - hr;
  const right = Math.max(...xs) + hr;
  const top = Math.min(...zs) - hr;
  const bottom = Math.max(...zs) + hr;
  return {
    x: texX(left),
    y: texY(top),
    w: u(right - left),
    h: u(bottom - top),
    rO: u(9)
  };
}

/** Arredonda as 4 quinas externas da cidade (cobre os "bicos" de asfalto). */
function roundCityEdge(ctx) {
  const b = cityBoundary();
  const r = b.rO;
  const { x, y, w, h } = b;
  ctx.save();
  ctx.beginPath();
  // retângulo do canvas inteiro...
  ctx.rect(0, 0, ctx.canvas.width, ctx.canvas.height);
  // ...e o retângulo arredondado da cidade como SUB-PATH (sem beginPath, que
  // apagaria o rect acima e inverteria o recorte evenodd).
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.clip('evenodd'); // região = canvas MENOS cidade = FORA da cidade
  ctx.fillStyle = COLORS.forest;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
}

/**
 * Borda preta + linha cinza no lado EXTERNO das ruas limítrofes, igual às
 * quadras internas (borda escura no limite + linha clara 1u para dentro/rua).
 */
function drawMapBorder(ctx) {
  const b = cityBoundary();
  ctx.lineWidth = Math.max(2, u(0.8));
  ctx.strokeStyle = COLORS.blockEdge;
  roundRectPath(ctx, b.x, b.y, b.w, b.h, b.rO);
  ctx.stroke();
  ctx.lineWidth = Math.max(1, u(0.18));
  ctx.strokeStyle = 'rgba(233,242,236,0.42)';
  roundRectPath(ctx, b.x + u(1), b.y + u(1), b.w - u(2), b.h - u(2), b.rO - u(1));
  ctx.stroke();
}

/**
 * Prolonga as vias laterais (ENTRY_ROADS) para fora do mapa, com um degradê
 * para a cor da floresta perto da borda do mundo — parecem se estender ao
 * infinito. São os pontos de entrada/saída dos jogadores. O asfalto cobre a
 * borda do mapa no cruzamento, abrindo a "saída".
 */
function drawEntryRoads(ctx) {
  const rw = u(ROADS.width);
  const half = rw / 2;
  const off = u(ROADS.width / 2 - 1); // posição da linha de bordo (como nas internas)
  const bx = Math.max(...ROADS.vertical.map((r) => r.x)) + ROADS.width / 2; // 120

  for (const er of ENTRY_ROADS) {
    const cy = texY(er.z);
    // Reseta o tracejado herdado das marcações (senão as bordas saem pontilhadas)
    ctx.setLineDash([]);

    for (const side of er.sides) {
      const left = side === 'left';
      const xWorld = texX(left ? -W / 2 : W / 2); // ponta (borda do mundo)
      const xBound = texX(left ? -bx : bx); // limite do mapa (margens param aqui)
      const xConn = texX(left ? -bx + 1.5 : bx - 1.5); // asfalto entra 1,5u

      // asfalto + bordas com a "boca" alargada e arredondada no encontro com a
      // cidade (mesmo raio das quinas das quadras: escura=6, cinza=7).
      const a0 = Math.min(xWorld, xConn);
      const a1 = Math.max(xWorld, xConn);
      const xGray = texX(left ? -(bx - 1) : bx - 1); // linha cinza da cidade (1u p/ dentro)
      entryMouthH(ctx, cy, half, off, xWorld, xBound, xConn, xGray, u(6));

      // faixa central tracejada — TERMINA na borda do cruzamento da via de
      // perímetro (igual às ruas internas), em vez de entrar um pedaço no
      // cruzamento e parar (que era a "falha" na conexão).
      const blockHalf = ROADS.width / 2 + 3.2 + 0.6;
      const perimX = left
        ? Math.min(...ROADS.vertical.map((r) => r.x))
        : Math.max(...ROADS.vertical.map((r) => r.x));
      const cEnd = texX(left ? perimX - blockHalf : perimX + blockHalf);
      ctx.save();
      ctx.strokeStyle = COLORS.lane;
      ctx.lineWidth = Math.max(2, u(0.3));
      ctx.setLineDash([u(2.6), u(2.8)]);
      hline(ctx, Math.min(xWorld, cEnd), Math.max(xWorld, cEnd), cy);
      ctx.restore();

      // degradê SUTIL só na pontinha (não altera o tom do restante da rua)
      const fade = u(14);
      const g = ctx.createLinearGradient(
        xWorld + (left ? fade : -fade),
        0,
        xWorld,
        0
      );
      g.addColorStop(0, hexA(COLORS.forest, 0));
      g.addColorStop(1, hexA(COLORS.forest, 1));
      ctx.fillStyle = g;
      ctx.fillRect(a0, cy - half - u(1), a1 - a0, rw + u(2));
    }
  }
}

/**
 * Versão VERTICAL de drawEntryRoads: prolonga as vias verticais (ENTRY_ROADS_V)
 * para fora do mapa em cima/embaixo. Mesmo desenho das laterais, transposto.
 */
function drawEntryRoadsV(ctx) {
  const rw = u(ROADS.width);
  const half = rw / 2;
  const off = u(ROADS.width / 2 - 1);
  const bz = Math.max(...ROADS.horizontal.map((r) => r.z)) + ROADS.width / 2; // 74

  for (const ev of ENTRY_ROADS_V) {
    const cx = texX(ev.x);
    ctx.setLineDash([]);

    for (const side of ev.sides) {
      const top = side === 'top';
      const zWorld = texY(top ? -D / 2 : D / 2); // ponta (borda do mundo)
      const zBound = texY(top ? -bz : bz);        // limite do mapa
      const zConn = texY(top ? -bz + 1.5 : bz - 1.5); // asfalto entra 1,5u

      const a0 = Math.min(zWorld, zConn);
      const a1 = Math.max(zWorld, zConn);
      const zGray = texY(top ? -(bz - 1) : bz - 1);
      entryMouthV(ctx, cx, half, off, zWorld, zBound, zConn, zGray, u(6));

      // faixa central tracejada (vertical) — termina na borda do cruzamento
      const blockHalf = ROADS.width / 2 + 3.2 + 0.6;
      const perimZ = top
        ? Math.min(...ROADS.horizontal.map((r) => r.z))
        : Math.max(...ROADS.horizontal.map((r) => r.z));
      const cEnd = texY(top ? perimZ - blockHalf : perimZ + blockHalf);
      ctx.save();
      ctx.strokeStyle = COLORS.lane;
      ctx.lineWidth = Math.max(2, u(0.3));
      ctx.setLineDash([u(2.6), u(2.8)]);
      vline(ctx, Math.min(zWorld, cEnd), Math.max(zWorld, cEnd), cx);
      ctx.restore();

      // degradê sutil só na pontinha
      const fade = u(6);
      const g = ctx.createLinearGradient(0, zWorld + (top ? fade : -fade), 0, zWorld);
      g.addColorStop(0, hexA(COLORS.forest, 0));
      g.addColorStop(1, hexA(COLORS.forest, 1));
      ctx.fillStyle = g;
      ctx.fillRect(cx - half - u(1), a0, rw + u(2), a1 - a0);
    }
  }
}

function hline(ctx, x1, x2, y) {
  ctx.beginPath();
  ctx.moveTo(x1, y);
  ctx.lineTo(x2, y);
  ctx.stroke();
}

function vline(ctx, y1, y2, x) {
  ctx.beginPath();
  ctx.moveTo(x, y1);
  ctx.lineTo(x, y2);
  ctx.stroke();
}

/**
 * "Boca" alargada da via de ENTRADA HORIZONTAL no encontro com a cidade: o asfalto
 * abre com cantos arredondados (raio r) e as bordas (escura no asfalto e cinza 1u
 * para dentro, raio r+1) acompanham o arco — igual às QUINAS das quadras — emendando
 * nas linhas do perímetro. Sem isso, arredondar a borda a soltava da rua (funil).
 *  cy=centro(z), half=meia-largura, off=recuo da cinza, xWorld=ponta na floresta,
 *  xBound=limite do mapa, xConn=asfalto entrando 1,5u, xGray=cinza do perímetro.
 */
function entryMouthH(ctx, cy, half, off, xWorld, xBound, xConn, xGray, r) {
  const toCity = Math.sign(xConn - xWorld);
  const fs = xBound - toCity * r; // início do flare (lado escuro)
  ctx.fillStyle = COLORS.asphalt;
  ctx.beginPath();
  ctx.moveTo(xWorld, cy - half);
  ctx.lineTo(fs, cy - half);
  ctx.arcTo(xBound, cy - half, xBound, cy - half - r, r);
  ctx.lineTo(xConn, cy - half - r);
  ctx.lineTo(xConn, cy + half + r);
  ctx.lineTo(xBound, cy + half + r);
  ctx.arcTo(xBound, cy + half, fs, cy + half, r);
  ctx.lineTo(xWorld, cy + half);
  ctx.closePath();
  ctx.fill();

  ctx.lineWidth = Math.max(2, u(0.8));
  ctx.strokeStyle = COLORS.blockEdge;
  ctx.beginPath();
  ctx.moveTo(xWorld, cy - half);
  ctx.lineTo(fs, cy - half);
  ctx.arcTo(xBound, cy - half, xBound, cy - half - r, r);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(xWorld, cy + half);
  ctx.lineTo(fs, cy + half);
  ctx.arcTo(xBound, cy + half, xBound, cy + half + r, r);
  ctx.stroke();

  const rg = r + u(1);
  const fsg = xGray - toCity * rg;
  ctx.lineWidth = Math.max(1, u(0.18));
  ctx.strokeStyle = 'rgba(233,242,236,0.42)';
  ctx.beginPath();
  ctx.moveTo(xWorld, cy - off);
  ctx.lineTo(fsg, cy - off);
  ctx.arcTo(xGray, cy - off, xGray, cy - off - rg, rg);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(xWorld, cy + off);
  ctx.lineTo(fsg, cy + off);
  ctx.arcTo(xGray, cy + off, xGray, cy + off + rg, rg);
  ctx.stroke();
}

/** Versão VERTICAL de entryMouthH (transposta x<->z). */
function entryMouthV(ctx, cx, half, off, zWorld, zBound, zConn, zGray, r) {
  const toCity = Math.sign(zConn - zWorld);
  const fs = zBound - toCity * r;
  ctx.fillStyle = COLORS.asphalt;
  ctx.beginPath();
  ctx.moveTo(cx - half, zWorld);
  ctx.lineTo(cx - half, fs);
  ctx.arcTo(cx - half, zBound, cx - half - r, zBound, r);
  ctx.lineTo(cx - half - r, zConn);
  ctx.lineTo(cx + half + r, zConn);
  ctx.lineTo(cx + half + r, zBound);
  ctx.arcTo(cx + half, zBound, cx + half, fs, r);
  ctx.lineTo(cx + half, zWorld);
  ctx.closePath();
  ctx.fill();

  ctx.lineWidth = Math.max(2, u(0.8));
  ctx.strokeStyle = COLORS.blockEdge;
  ctx.beginPath();
  ctx.moveTo(cx - half, zWorld);
  ctx.lineTo(cx - half, fs);
  ctx.arcTo(cx - half, zBound, cx - half - r, zBound, r);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + half, zWorld);
  ctx.lineTo(cx + half, fs);
  ctx.arcTo(cx + half, zBound, cx + half + r, zBound, r);
  ctx.stroke();

  const rg = r + u(1);
  const fsg = zGray - toCity * rg;
  ctx.lineWidth = Math.max(1, u(0.18));
  ctx.strokeStyle = 'rgba(233,242,236,0.42)';
  ctx.beginPath();
  ctx.moveTo(cx - off, zWorld);
  ctx.lineTo(cx - off, fsg);
  ctx.arcTo(cx - off, zGray, cx - off - rg, zGray, rg);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + off, zWorld);
  ctx.lineTo(cx + off, fsg);
  ctx.arcTo(cx + off, zGray, cx + off + rg, zGray, rg);
  ctx.stroke();
}

function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Itera todas as vias como segmentos {axis,c,min,max}. */
function forEachRoad(cb) {
  for (const r of ROADS.vertical) {
    cb({
      axis: 'v',
      c: r.x,
      min: r.zMin ?? -D / 2,
      max: r.zMax ?? D / 2
    });
  }
  for (const r of ROADS.horizontal) {
    cb({
      axis: 'h',
      c: r.z,
      min: r.xMin ?? -W / 2,
      max: r.xMax ?? W / 2
    });
  }
}

/** Desenha um segmento de via. inner!=null => apenas as bordas (vinheta). */
function bandSeg(ctx, seg, thickness, inner = null) {
  if (seg.axis === 'h') {
    const cy = texY(seg.c);
    const x0 = texX(seg.min);
    const x1 = texX(seg.max);
    if (inner === null) {
      ctx.fillRect(x0, cy - thickness / 2, x1 - x0, thickness);
    } else {
      const edge = (thickness - inner) / 2;
      ctx.fillRect(x0, cy - thickness / 2, x1 - x0, edge);
      ctx.fillRect(x0, cy + inner / 2, x1 - x0, edge);
    }
  } else {
    const cx = texX(seg.c);
    const y0 = texY(seg.min);
    const y1 = texY(seg.max);
    if (inner === null) {
      ctx.fillRect(cx - thickness / 2, y0, thickness, y1 - y0);
    } else {
      const edge = (thickness - inner) / 2;
      ctx.fillRect(cx - thickness / 2, y0, edge, y1 - y0);
      ctx.fillRect(cx + inner / 2, y0, edge, y1 - y0);
    }
  }
}

/* ---------------------------- faixas ------------------------------- */

function drawMarkings(ctx) {
  // Zona livre ao redor de cada cruzamento: cobre o miolo + a faixa de
  // pedestres, para que nenhuma linha de faixa atravesse o cruzamento.
  const half = ROADS.width / 2;
  // Reserva do MIOLO do cruzamento — inalterada. O respiro das faixas NÃO entra aqui: em
  // cidade sem superquadra não existe faixa de pedestre, e alargar este vão mexeria no
  // tracejado das três cidades que não têm o problema. Onde há faixa de braço, o vão dela
  // (em allowedIntervals) estende este buraco só naquele lado.
  const blockHalf = half + CROSSWALK_DEPTH + 0.6;

  // Linha central tracejada (interrompida nos cruzamentos). As linhas de
  // bordo agora são desenhadas por quadra (acompanhando o arredondado) em
  // drawSidewalks, então aqui fica só o eixo.
  ctx.strokeStyle = COLORS.lane;
  ctx.lineWidth = Math.max(2, u(0.3));
  ctx.setLineDash([u(2.6), u(2.8)]);
  forEachRoad((seg) => {
    for (const [a, b] of allowedIntervals(seg, blockHalf)) {
      // trecho que ficou DENTRO de uma superquadra não é mais rua (virou parque)
      const mid = (a + b) / 2;
      if (insideDistrict(seg.axis === 'v' ? seg.c : mid, seg.axis === 'v' ? mid : seg.c)) continue;
      drawSegLine(ctx, seg, 0, a, b);
    }
  });
}

/**
 * Coordenadas (no eixo da via) onde outra via cruza esta.
 *
 * Só conta quem é RUA DE VERDADE (`isDrivable`). Numa cidade com superquadra os eixos que
 * viraram parque continuam na lista de vias — é assim que `cityLayout` deriva colunas e
 * linhas dos lotes —, mas ali não há asfalto cruzando: a linha central era interrompida
 * num cruzamento que não existe. Na Mexican são só x=±112,0 e z=±80,0 que são rua, então
 * cada marginal ganhava 4 buracos falsos (nos eixos ±37 e ±69).
 *
 * É o MESMO critério que as faixas de pedestre já usavam (ver drawCrosswalks, que filtra
 * por `isDrivable && isGridLine`) — agora a linha central concorda com elas.
 */
function crossingsOn(seg) {
  const cross = [];
  if (seg.axis === 'v') {
    for (const h of ROADS.horizontal) {
      if (!isDrivable(h)) continue;
      const xMin = h.xMin ?? -W / 2;
      const xMax = h.xMax ?? W / 2;
      if (seg.c < xMin || seg.c > xMax) continue;
      if (h.z < seg.min || h.z > seg.max) continue;
      cross.push(h.z);
    }
  } else {
    for (const v of ROADS.vertical) {
      if (!isDrivable(v)) continue;
      const zMin = v.zMin ?? -D / 2;
      const zMax = v.zMax ?? D / 2;
      if (seg.c < zMin || seg.c > zMax) continue;
      if (v.x < seg.min || v.x > seg.max) continue;
      cross.push(v.x);
    }
  }
  return cross.sort((a, b) => a - b);
}

/**
 * Intervalos [a,b] (no eixo da via) a marcar, excluindo a zona dos cruzamentos E as FAIXAS
 * DE PEDESTRE.
 *
 * O buraco do cruzamento é largo (`blockHalf`, que já inclui a profundidade da faixa nos
 * braços). O da faixa é a profundidade dela mais CROSSWALK_CLEAR de cada lado — o respiro que
 * separa as duas marcações, sem apagar o eixo do quarteirão inteiro. As faixas dos braços
 * caem dentro do buraco do cruzamento; quem passa a ser respeitado são as travessias
 * alinhadas às ruas internas da superquadra, que antes eram atravessadas pelo tracejado.
 */
function allowedIntervals(seg, blockHalf) {
  const faixaHalf = CROSSWALK_DEPTH / 2 + CROSSWALK_CLEAR;
  const vaos = crossingsOn(seg).map((c) => [c - blockHalf, c + blockHalf]);
  for (const sp of crosswalkSpots()) {
    if (sp.axis !== seg.axis || Math.abs(sp.c - seg.c) > 1e-6) continue; // faixa de outra via
    vaos.push([sp.at - faixaHalf, sp.at + faixaHalf]);
  }
  vaos.sort((a, b) => a[0] - b[0]);
  const out = [];
  let start = seg.min;
  for (const [a, b] of vaos) {
    if (a > start) out.push([start, a]);
    start = Math.max(start, b);
  }
  if (start < seg.max) out.push([start, seg.max]);
  return out;
}

/** Linha ao longo do eixo da via (de `from` a `to`), com deslocamento lateral. */
function drawSegLine(ctx, seg, lateral, from, to) {
  if (seg.axis === 'v') {
    const x = texX(seg.c + lateral);
    line(ctx, x, texY(from), x, texY(to));
  } else {
    const y = texY(seg.c + lateral);
    line(ctx, texX(from), y, texX(to), y);
  }
}

function line(ctx, x1, y1, x2, y2) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

/* ------------------------ faixas de pedestre ----------------------- */

/** Profundidade da faixa de pedestre ao longo da via (unidades de mundo). */
const CROSSWALK_DEPTH = 3.2;
/**
 * RESPIRO entre a faixa de pedestre e a marcação de pista (unidades de mundo).
 *
 * A marcação para a esta distância da BORDA da zebra, dos dois lados. Não é folga numérica —
 * é espaçamento visual: com o valor antigo (0,6) o tracejado encostava na zebra e as duas
 * marcações liam como uma só mancha. Como `setLineDash` reinicia a fase em cada traço, o
 * primeiro risco nasce exatamente na borda do intervalo, então este número é o branco que
 * aparece na tela. 1,6 é da ordem de meio vão do tracejado (padrão 2,6/2,8) — lê como
 * espaçamento proposital, e sobra tracejado de folga: no menor trecho da Mexican, entre o
 * buraco do cruzamento e a travessia da rua interna, restam ~25u de eixo pintado.
 */
const CROSSWALK_CLEAR = 1.6;

/**
 * FONTE ÚNICA das faixas de pedestre: `{axis, c, at}` — `axis`/`c` identificam a VIA que a
 * faixa atravessa (a zebra cobre a largura dela) e `at` é a posição da faixa AO LONGO dessa
 * via. A profundidade é sempre CROSSWALK_DEPTH.
 *
 * Por que uma fonte única: `drawCrosswalks` pinta daqui e `drawMarkings` abre o buraco na
 * linha central daqui. Enquanto eram duas listas, elas discordavam — a linha central só era
 * interrompida nos cruzamentos de vias DIRIGÍVEIS (`crossingsOn`), então as travessias
 * alinhadas às ruas INTERNAS da superquadra (o "complemento" abaixo, em vias que não são
 * asfalto) não abriam buraco nenhum e o tracejado atravessava a zebra. Medido na Mexican:
 * 6 faixas com marcação de pista por dentro, em (±37,0), (±69,0) e (0,±37).
 *
 * As regras de QUANDO existe faixa são as de trânsito, e continuam idênticas:
 *   R1: só em via com CARRO           → isDrivable && isGridLine.
 *   R2/R4: travessia ENTRE QUADRAS    → exige DISTRITO dos dois lados da via cruzada (±S).
 *   R3: miolo do cruzamento LIMPO     → a zebra é deslocada para a boca da quadra (OFF).
 */
function crosswalkSpots() {
  const spots = [];
  const hw = ROADS.width / 2;                 // meia-via (mundo)
  const OFFw = hw + CROSSWALK_DEPTH / 2;      // deslocamento p/ o braço (=6.6)
  const S = hw + 1;                           // distância lateral p/ exigir quadra dos 2 lados

  const DV = ROADS.vertical.filter((r) => isDrivable(r) && isGridLine(r));
  const DH = ROADS.horizontal.filter((r) => isDrivable(r) && isGridLine(r));

  // BRAÇOS dos cruzamentos de vias dirigíveis
  for (const vr of DV) {
    const vMin = vr.zMin ?? -D / 2, vMax = vr.zMax ?? D / 2;
    for (const hr of DH) {
      const hMin = hr.xMin ?? -W / 2, hMax = hr.xMax ?? W / 2;
      if (hr.z < vMin || hr.z > vMax) continue; // não se cruzam
      if (vr.x < hMin || vr.x > hMax) continue;
      const cx0 = vr.x, cz0 = hr.z;
      // o braço existe só se a via CONTINUA para aquele lado (evita faixa apontando p/ o nada)
      const up = vMin < cz0 - hw, down = vMax > cz0 + hw;
      const left = hMin < cx0 - hw, right = hMax > cx0 + hw;
      // GUARDA quadra-dos-dois-lados: up/down cruzam a VERTICAL vr.x; left/right, a HORIZONTAL hr.z.
      if (up && insideDistrict(cx0 - S, cz0 - OFFw) && insideDistrict(cx0 + S, cz0 - OFFw))
        spots.push({ axis: 'v', c: cx0, at: cz0 - OFFw });
      if (down && insideDistrict(cx0 - S, cz0 + OFFw) && insideDistrict(cx0 + S, cz0 + OFFw))
        spots.push({ axis: 'v', c: cx0, at: cz0 + OFFw });
      if (left && insideDistrict(cx0 - OFFw, cz0 - S) && insideDistrict(cx0 - OFFw, cz0 + S))
        spots.push({ axis: 'h', c: cz0, at: cx0 - OFFw });
      if (right && insideDistrict(cx0 + OFFw, cz0 - S) && insideDistrict(cx0 + OFFw, cz0 + S))
        spots.push({ axis: 'h', c: cz0, at: cx0 + OFFw });
    }
  }

  // COMPLEMENTO: travessias alinhadas às RUAS INTERNAS das superquadras. Um avatar anda numa
  // via interna (calçada) e cruza a avenida de carros para a superquadra vizinha, na MESMA
  // direção. A faixa fica CENTRADA na avenida, no x/z da via interna, e só onde há superquadra
  // ALINHADA dos dois lados. No anel a guarda reprova (do outro lado é floresta).
  const NDV = ROADS.vertical.filter((r) => !isDrivable(r) && isGridLine(r));   // internas verticais
  const NDH = ROADS.horizontal.filter((r) => !isDrivable(r) && isGridLine(r)); // internas horizontais
  for (const av of DH) {          // avenida dirigível horizontal
    const aMin = av.xMin ?? -W / 2, aMax = av.xMax ?? W / 2;
    for (const p of NDV) {
      if (p.x < aMin || p.x > aMax) continue;
      if (insideDistrict(p.x, av.z - S) && insideDistrict(p.x, av.z + S))
        spots.push({ axis: 'h', c: av.z, at: p.x });   // pedestre cruza N-S
    }
  }
  for (const av of DV) {          // avenida dirigível vertical
    const aMin = av.zMin ?? -D / 2, aMax = av.zMax ?? D / 2;
    for (const p of NDH) {
      if (p.z < aMin || p.z > aMax) continue;
      if (insideDistrict(av.x - S, p.z) && insideDistrict(av.x + S, p.z))
        spots.push({ axis: 'v', c: av.x, at: p.z });   // pedestre cruza L-O
    }
  }
  return spots;
}

// Faixas de pedestre pelas REGRAS GERAIS de trânsito: modelo BASEADO EM BRAÇOS.
//   R1: só em via com CARRO           → filtra isDrivable && isGridLine (sobra anel + cruz central;
//                                        os eixos internos ±37/±69/z±37 viraram calçada, saem).
//   R2/R4: travessia ENTRE QUADRAS    → em cada cruzamento de vias dirigíveis, pinta o braço só se
//                                        houver DISTRITO DOS DOIS LADOS da via cruzada (guarda
//                                        insideDistrict a ±S). Isso mata todo braço do anel/quina que
//                                        dá para a floresta e mantém os 4 do centro + 1 por entrada.
//   R3: miolo LIMPO                    → a zebra é DESLOCADA para a boca da quadra (OFF = half +
//                                        stripeLen/2), nunca centrada no cruzamento → sem o "田".
function drawCrosswalks(ctx) {
  const half = u(ROADS.width / 2);            // meia-via (px)
  const stripeLen = u(CROSSWALK_DEPTH);
  const stripeW = u(0.7);
  const gap = u(1.2);
  for (const sp of crosswalkSpots()) {
    // a zebra cobre a LARGURA da via `sp.axis` e tem CROSSWALK_DEPTH ao longo dela: via
    // vertical -> tarjas distribuidas em x (dir 'h'); horizontal -> em z (dir 'v').
    if (sp.axis === 'v') zebra(ctx, texX(sp.c), texY(sp.at), 'h', stripeW, stripeLen, gap, half);
    else zebra(ctx, texX(sp.at), texY(sp.c), 'v', stripeW, stripeLen, gap, half);
  }
}

function zebra(ctx, cx, cy, dir, stripeW, stripeLen, gap, halfRoad) {
  ctx.fillStyle = 'rgba(226,235,229,0.55)';
  const span = halfRoad * 2 - u(1.2);
  const count = Math.floor(span / (stripeW + gap));
  const start = -((count - 1) * (stripeW + gap)) / 2;
  for (let i = 0; i < count; i++) {
    const o = start + i * (stripeW + gap);
    if (dir === 'h') ctx.fillRect(cx + o - stripeW / 2, cy - stripeLen / 2, stripeW, stripeLen);
    else ctx.fillRect(cx - stripeLen / 2, cy + o - stripeW / 2, stripeLen, stripeW);
  }
}

/* ------------------------------ setas ------------------------------ */

/**
 * Setas de direção pintadas no asfalto. As posições saem da própria malha (não
 * são fixas): nas VERTICAIS, a 2ª e a penúltima via, na altura do meio da 1ª e da
 * última linha; nas HORIZONTAIS, a via z=23 no meio da 2ª e da penúltima coluna.
 * Numa grade de 5 colunas isso dá exatamente as posições de sempre (x=∓69 / ∓46);
 * numa de 4 colunas as setas acompanham as vias que existem lá.
 */
function drawArrows(ctx) {
  ctx.fillStyle = 'rgba(226,235,229,0.6)';
  const laneOff = u(ROADS.width / 4);
  const xs = GRID_V.map((r) => r.x);
  const zs = GRID_H.map((r) => r.z);
  const mid = (a, b) => (a + b) / 2;
  const vxL = xs[1], vxR = xs[xs.length - 2]; // 2ª e penúltima verticais
  const rowTop = mid(zs[0], zs[1]); // meio da 1ª linha
  const rowBot = mid(zs[zs.length - 2], zs[zs.length - 1]); // meio da última linha
  const v = [
    { x: vxL, z: rowTop, dir: 'up' },
    { x: vxL, z: rowBot, dir: 'down' },
    { x: vxR, z: rowTop, dir: 'down' },
    { x: vxR, z: rowBot, dir: 'up' }
  ];
  for (const a of v) {
    if (insideDistrict(a.x, a.z)) continue; // virou parque: não é mais pista
    arrow(ctx, texX(a.x) + laneOff, texY(a.z), a.dir);
  }
  const hz = zs[zs.length - 2]; // via horizontal z=23
  const hh = [
    { x: mid(xs[1], xs[2]), z: hz, dir: 'right' }, // meio da 2ª coluna
    { x: mid(xs[xs.length - 3], xs[xs.length - 2]), z: hz, dir: 'left' } // penúltima coluna
  ];
  for (const a of hh) {
    if (insideDistrict(a.x, a.z)) continue;
    arrow(ctx, texX(a.x), texY(a.z) + laneOff, a.dir);
  }
}

function arrow(ctx, cx, cy, dir) {
  const L = u(2.8);
  const Wd = u(1.7);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate({ up: 0, down: Math.PI, left: -Math.PI / 2, right: Math.PI / 2 }[dir]);
  ctx.fillRect(-u(0.3), -L * 0.1, u(0.6), L);
  ctx.beginPath();
  ctx.moveTo(0, -L * 0.65);
  ctx.lineTo(-Wd / 2, -L * 0.1);
  ctx.lineTo(Wd / 2, -L * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/* ---------------------------- rotatória ---------------------------- */

/** Praça: círculo central pavimentado + caminhos em cruz até as calçadas. */
// Chão de PRAÇA reutilizável: cruz pavimentada + largo central circular ladrilhado
// com contorno escuro. Usado na rotatória das outras cidades E nas células-parque
// da superquadra Mexican (mesma estética das outras praças do jogo).
function drawPlazaGround(ctx, wx, wz, reachU = 14, Ru = 7) {
  const cx = texX(wx);
  const cy = texY(wz);
  const R = u(Ru);
  const pw = u(3.6);
  const reach = u(reachU);

  // caminho em CRUZ (liga o centro às calçadas) + LARGO central — tudo em calçada
  const plazaPath = () => {
    ctx.beginPath();
    ctx.rect(cx - pw / 2, cy - reach, pw, reach * 2);
    ctx.rect(cx - reach, cy - pw / 2, reach * 2, pw);
    ctx.moveTo(cx + R, cy);
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
  };
  ctx.fillStyle = COLORS.sidewalk;
  plazaPath();
  ctx.fill();

  // LADRILHOS por TODO o piso da praça (cruz + largo), não só no largo central
  ctx.save();
  plazaPath();
  ctx.clip();
  drawTileGrid(ctx, cx - reach, cy - reach, reach * 2, reach * 2);
  ctx.restore();

  // contorno escuro do largo
  ctx.strokeStyle = 'rgba(0,0,0,0.2)';
  ctx.lineWidth = Math.max(1.5, u(0.35));
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.stroke();
}

function drawPlaza(ctx) {
  drawPlazaGround(ctx, ROUNDABOUT.x, ROUNDABOUT.z);
}

/* ------------------------- sombras de contato ---------------------- */

function drawContactShadows(ctx) {
  for (const b of BUILDINGS) {
    if (b.type === 'house' || b.type === 'houses') continue; // sombras reais
    const cx = texX(b.x);
    const cy = texY(b.z) + u(1.5);
    const R = u(Math.max(b.w, b.d) / 2 + 3);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    grad.addColorStop(0, 'rgba(0,0,0,0.42)');
    grad.addColorStop(0.7, 'rgba(0,0,0,0.2)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fill();
  }
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Grade de ladrilhos quadrados (juntas claras/escuras) para as calçadas. */
function drawTileGrid(ctx, x, y, w, h) {
  const tile = u(1.0); // ladrilho miúdo (~3 por avatar) — mais proporcional (era 2.6 → 1.3 → 1.0)
  ctx.lineWidth = Math.max(1, u(0.1));
  ctx.strokeStyle = 'rgba(0,0,0,0.16)';
  ctx.beginPath();
  for (let px = x; px <= x + w; px += tile) {
    ctx.moveTo(px, y);
    ctx.lineTo(px, y + h);
  }
  for (let py = y; py <= y + h; py += tile) {
    ctx.moveTo(x, py);
    ctx.lineTo(x + w, py);
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.beginPath();
  for (let px = x + 1.5; px <= x + w; px += tile) {
    ctx.moveTo(px, y);
    ctx.lineTo(px, y + h);
  }
  for (let py = y + 1.5; py <= y + h; py += tile) {
    ctx.moveTo(x, py);
    ctx.lineTo(x + w, py);
  }
  ctx.stroke();
}
